import React, { useRef, useState } from 'react';
import { Box, Flex } from 'rebass';
import axios from 'axios';

import { getAttributeValue, formToJSON } from './utils'
import {
	WebformDebug,
	WebformInput,
	WebformSelect,
	WebformTextarea,
	WebformCheckbox,
	WebformCheckboxGroup,
	WebformText,
	WebformMarkup,
	WebformFile,
	WebformSelectOther
} from './components';
import Button from "../../components/button";
import { omit } from "ramda";
import ReCAPTCHA from "react-google-recaptcha";
import ElementWrapper from "./components/webformElementWrapper";

export const DEFAULT_SUBMIT_LABEL = 'Submit';

export class WebformError extends Error {
	response;

	constructor(response) {
		super();

		this.response = response
	}
}

export function renderWebformElement({ element, error, CustomComponent, debug, color, columns }) {
	const customComponentAPI = {
		error,
		color,
		columns
	};

	// Render using custom compoennt if provided:
	if (CustomComponent) {
		return <CustomComponent element={element} {...customComponentAPI} />
	}

	// Othervise select renderer based on element type:
	switch (element.type) {
		case 'textfield':
			return <WebformInput element={{ ...element, type: 'text' }} {...customComponentAPI} />;
		case 'textarea':
			return <WebformTextarea element={element} {...customComponentAPI} />;
		case 'tel':
		case 'number':
		case 'email':
		case 'hidden':
		case 'webform_email_multiple':
			return <WebformInput element={element} {...customComponentAPI} />;
		case 'checkbox':
		case 'radio':
			/** Render single checkbox or radio element. */
			return <WebformCheckbox element={element} {...customComponentAPI} />;
		case 'checkboxes':
			return <WebformCheckboxGroup element={{ ...element, type: 'checkbox' }} {...customComponentAPI} />;
		case 'radios':
			return <WebformCheckboxGroup element={{ ...element, type: 'radio' }} {...customComponentAPI} />;
		case 'select':
			return <WebformSelect element={element} {...customComponentAPI} />;
		case 'webform_select_other':
			return <WebformSelectOther element={element} {...customComponentAPI} />;
		case 'processed_text':
			return <WebformText element={element} {...customComponentAPI} />;
		case 'webform_markup':
			return <WebformMarkup element={element} {...customComponentAPI} />;
		case 'webform_document_file':
			return <WebformFile element={element} {...customComponentAPI}/>;
		case 'webform_actions':
			return (
				<Flex px={2} justifyContent={['center', null, "flex-end"]} alignSelf="center" flexGrow={1}>
					<Button type="submit" variant="primary" color={color} fontSize={0}>{getAttributeValue('#submit__label', element) || DEFAULT_SUBMIT_LABEL}</Button>
				</Flex>
			);
		// Unknown element type -> render as json string
		default:
			return debug ? <WebformDebug element={element} error={error} /> : null
	}
}

/**
 * Drupal webform react component.
 */
const Webform = ({ webform, customComponents, debug, columns = 2, color = 'text',  ...props }) => {
	const [errors, setErrors] = useState({});
	const [recaptchaToken, setRecaptchaToken] = useState(null);
	const captchaRef = useRef(null);

	const submitHandler = async event => {
		event.preventDefault();

		const target = event.currentTarget;

		// Clear errors from previous submit.
		setErrors({});

		// Remove lingering css classes from previous submits.
		target.classList.remove('form-submitting', 'form-error', 'form-submitted');

		if ((!props.onValidate || props.onValidate(event)) && target.checkValidity()) {
			// Let css know that this form was validated and is being submitted.
			target.classList.add('was-validated', 'form-submitting');

			// Serialize form data.
			const data = await formToJSON(target.elements);

			try {
				// If onSubmit returns false skip submitting to API.
				if (props.onSubmit && (await props.onSubmit(data, event)) === false) {
					target.classList.replace('form-submitting', 'form-submitted');
					return
				}

				// Send request!
				// Submit form to API.

				// const response = await axios.post(props.endpoint, formData, {
				const response = await axios.post(props.endpoint, {
						...props.extraData,
						...data,
						webform_id: webform.drupal_internal__id,
					'g-recaptcha-response': recaptchaToken
				}, {
					headers: {
						'Authorization': 'Basic QmVsYXphcjpCZWxhemFy'
					}
				});

				if (response.data.error) {
					throw new WebformError(response)
				}

				// Convey current form state.
				target.classList.replace('form-submitting', 'form-submitted');
				props.onSuccess && props.onSuccess(response.data, event)
			} catch (err) {
				// API should return error structure if validation fails.
				// We use that to render error messages to the form.
				if (err.response && err.response.data.error) {
					setErrors(err.response.data.error)
				}

				// Convey current form state.
				target.classList.replace('form-submitting', 'form-error');
				props.onError && props.onError(err, event);
				setRecaptchaToken(null);
				captchaRef.current && captchaRef.current.reset();
			}
		} else {
			// Let css know this form was validated.
			target.classList.add('was-validated', 'form-error')
		}
	};

	return (
		<Flex
			as="form"
			flexWrap="wrap"
			alignItems="flex-start"
			onSubmit={submitHandler}
			noValidate={props.noValidate}
			data-webform-id={props.id}
			{...omit(['onSuccess'], props)}
		>
			{/* Render webform elements */}
			{webform.elements.map(element => (
				<React.Fragment key={element.name}>
					{renderWebformElement({
						element,
						error: errors[element.name],
						CustomComponent: (customComponents && customComponents[element.name]),
						debug,
						columns,
						color
					})}
				</React.Fragment>
			))}
			{/* Render recatcha */}

			<ElementWrapper
				error={errors['g-recaptcha-response']}
				settings={{ attributes: {}, states: {}, pure: false }}
				color={color}
			>
				<ReCAPTCHA
					ref={captchaRef}
					sitekey={process.env.GATSBY_RECAPTCHA_KEY}
					onChange={setRecaptchaToken}
				/>
			</ElementWrapper>

			{/* Render default submit button if it is not defined in elements array. */}
			{webform.elements.find(element => element.type === 'webform_actions') === undefined && (
				<Box px={2}>
					<Button type="submit">{DEFAULT_SUBMIT_LABEL}</Button>
				</Box>
			)}
		</Flex>
	)
};

Webform.defaultProps = {
	customComponents: {}
};

export default Webform;
